<template>
  <div class="uk-margin-medium-top uk-grid-collapse uk-flex uk-flex-center uk-grid-match" uk-grid>

    <ErrorModal id="CreateVMErrorModal" :message="error_message" />

    <div class="uk-width-1-3@l uk-flex-first@l uk-flex-last">
      <div class="uk-tile uk-tile-primary">
        <div class="uk-flex uk-flex-center" uk-grid>

          <div class="uk-width-1-1 uk-flex uk-flex-center uk-margin-bottom">
            <img src="@/assets/images/desktop-icon.svg" height="160" uk-svg />
          </div>

          <div class="uk-width-1-1 uk-margin-large-top uk-margin-large-bottom">
            <ul class="uk-list uk-margin-large-bottom">
              <li class="uk-text-nowrap">Hostname: {{ hostname }}</li>
              <li>OS: {{ selectedOS.title }} {{ selectedOS.version.v }}</li>
              <hr />
              <li class="uk-grid-small" uk-grid>
                <div class="uk-width-expand" uk-leader="fill: _">{{ selectedCores }} Cores</div>
                <div>{{ cpuSubTotal | formatCurrency }}</div>
              </li>
              <li class="uk-grid-small" uk-grid>
                <div class="uk-width-expand" uk-leader="fill: _">{{ selectedRAM }}GB Memory</div>
                <div>{{ ramSubTotal | formatCurrency }}</div>
              </li>
              <li class="uk-grid-small" uk-grid>
                <div class="uk-width-expand" uk-leader="fill: _">{{ selectedStorage }}GB Storage</div>
                <div>{{ storageSubTotal | formatCurrency }}</div>
              </li>
              <li class="uk-grid-small" uk-grid>
                <div class="uk-width-expand" uk-leader="fill: _">Static IP Address</div>
                <div>free</div>
              </li>
              <hr />
              <li class="uk-grid-small" uk-grid>
                <div class="uk-width-expand" uk-leader="fill: _">Subtotal</div>
                <div>{{ subTotal | formatCurrency }}/mo.</div>
              </li>
            </ul>
          </div>

          <div class="uk-width-3-4@xl uk-width-2-3@m uk-flex uk-flex-center ba-position-bottom">
            <BitButton color="inverse" disabled v-if="!hostname_is_valid && !spin">Deploy</BitButton>
            <BitButton type="submit" v-if="hostname_is_valid && !spin" @click.native="submit_deploy_vm">Deploy</BitButton>
            <BitButton v-if="spin" disabled><div uk-spinner></div></BitButton>
          </div>

        </div>
      </div>
    </div>

    <div class="uk-width-2-3@l">
      <div class="uk-tile uk-tile-default">
        <div uk-grid>
          <div class="uk-width-1-1 uk-margin-small-bottom">
            <h2 class="uk-text-center">Create Virtual Machine</h2>
          </div>

          <form class="uk-width-1-1 uk-grid-medium" uk-grid v-on:submit.prevent>

            <div class="uk-width-1-1">
              <label class="uk-form-label" for="machinename">
                Hostname
                <a class="uk-icon-link" uk-icon="icon: info; ratio: .9;" uk-tooltip="title: your machine hostname will be set to this value when deployed; pos: top"></a>
                </label>
              <div class="uk-form-controls">
                <input class="uk-input" title="Hostname can only include alphanumeric characters, hyphens '-', underscores '_', and periods, and must be between 3 and 60 characters." type="text" name="machinename" placeholder="MyVirtualMachine" @keydown.space.prevent minlength="3" maxlength="60" pattern="[a-zA-Z0-9.-_]+" v-model="hostname" required />
              </div>
              <p class="uk-text-muted uk-text-small uk-text-danger uk-margin-small-top" v-if="!hostname_is_valid && !hostname_is_blank">Hostname can only include alphanumeric characters, hyphens '-', underscores '_', and periods, and must be between 3 and 60 characters.</p>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label" for="os">
                Operating System
                <a class="uk-icon-link" uk-icon="icon: info; ratio: .9;" uk-tooltip="title: all operating systems are x64 and the default selection is the latest LTS release; pos: top"></a>
              </label>
              <div class="uk-form-controls">
                <div class="uk-child-width-1-3@xl uk-child-width-1-2@s uk-grid-small" uk-grid>
                  <div v-for="(os_option, index) in configurable_options.operating_systems" :key="index">
                    <OSRadioButton :os="os_option" name="os_options" :value="selectedOS" @change="changeOS"></OSRadioButton>
                  </div>
                </div>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label" for="cores">
                CPU Cores
              </label>
              <div class="uk-form-controls">
                <div class="noUiSlider uk-padding-remove-left" ref="CoreSlider"></div>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label" for="ram">
                Memory
              </label>
              <div class="uk-form-controls">
                <div class="noUiSlider uk-padding-remove-left" ref="RAMSlider"></div>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label" for="ram">
                Storage
                <a class="uk-icon-link" uk-icon="icon: info; ratio: .9;" uk-tooltip="title: 50 GB of SSD storage is included for free with every virtual machine; pos: top"></a>
              </label>
              <div class="uk-form-controls">
                <div class="noUiSlider uk-padding-remove-left" ref="StorageSlider"></div>
              </div>
            </div>

          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import BitButton from '@/components/common/button.vue';
import ErrorModal from '@/components/portal/errormodal.vue';
import OSRadioButton from '@/components/portal/osradiobutton.vue';
import noUiSlider from 'nouislider';

import { mapActions, mapGetters, mapState } from 'vuex';

import UIkit from 'uikit';

export default {
  name: 'CreateVirtualMachine',
  components: {
    BitButton,
    ErrorModal,
    OSRadioButton
  },
  data() {
    return {
      spin: false,
      hostname: "",
      selectedOS: {
        name: "",
        title: "",
        version: {
          v: "",
          osType: ""
        }
      },
      selectedCores: 1,
      selectedRAM: 2,
      selectedStorage: 50,
      error_message: null
    }
  },
  computed: {
    ...mapState('vm', ['configurable_options']),
    ...mapState('credit', ['account_balance']),
    ...mapState('creditcard', ['credit_card']),
    ...mapGetters('creditcard', ['is_card_expired']),
    cpuSubTotal() {
      return this.selectedCores * this.configurable_options.core_rate
    },
    ramSubTotal() {
      return this.selectedRAM * this.configurable_options.ram_rate
    },
    storageSubTotal() {
      return ( this.selectedStorage - 50) * this.configurable_options.storage_rate
    },
    subTotal() {
      return this.cpuSubTotal + this.ramSubTotal + this.storageSubTotal
    },
    hostname_is_valid() {
      const regex = /^[a-zA-Z0-9-_.]+$/
      if( this.hostname == null )
        return false
      else if( regex.test(this.hostname) )
        return true
      return false
    },
    hostname_is_blank() {
      return this.hostname == ""
    }
  },
  created() {
    // get account balance and credit on reload just in-case
    this.GET_ACCOUNT_BALANCE();
    this.GET_CREDIT_CARD();
    this.GET_CONFIGURABLE_OPTIONS();
    this.initDefaultOS();
  },
  methods: {
    ...mapActions('credit', ['GET_ACCOUNT_BALANCE']),
    ...mapActions('creditcard', ['GET_CREDIT_CARD']),
    ...mapActions('vm', ['CREATE_VIRTUAL_MACHINE', 'GET_CONFIGURABLE_OPTIONS']),
    changeOS(os) {
      this.selectedOS = os;
    },
    changeCores(cores) {
      this.selectedCores = cores;
    },
    isBillingSufficient() {
      let authorize = this.subTotal;

      // if user has an account balance we use that first to check payment authorization
      if(this.account_balance > 0) {
        if(this.account_balance >= authorize) {
          return true
        } else {
          authorize -= this.account_balance;
        }
      }

      // if we didn't return true out of last block, there is still an amount in 'authorize' that would need to be paid
      // check if they have an active CC to charge that amount to
      if(this.credit_card && !this.is_card_expired) {
        return true
      }
      // default return false
      return false
    },
    initDefaultOS() {
      let def_os = this.configurable_options.operating_systems[2] // set default selection
      this.selectedOS.name = def_os.name,
      this.selectedOS.title = def_os.title,
      this.selectedOS.version = def_os.versions[0]
    },
    initCoreSlider() {
      noUiSlider.create(this.$refs.CoreSlider, {
        start: [1],
        animate: true,
        tooltips: [false],
        connect: [true, false],
        step: 1,
        range: {
          'min': 1,
          'max': 6
        },
        pips: {
          mode: 'steps',
          density: 24,
          format: {
            to: function (value) {
              return value + ( value == 1 ? ' Core' : ' Cores');
            },
            from: function (value) {
              return Number(value.slice(1));
            }
          }
        },
        format: {
          to: function (value) {
            return value
          },
          from: function (value) {
            return Number(value)
          }
        }
      });

      this.$refs.CoreSlider.noUiSlider.on('update', () => {
          this.selectedCores = this.$refs.CoreSlider.noUiSlider.get();
      });
    },
    initRAMSlider() {
      noUiSlider.create(this.$refs.RAMSlider, {
        start: 4,
        animate: true,
        snap: true,
        tooltips: [false],
        connect: [true, false],
        range: {
          'min': 2,
          '16.66%': 4,
          '33.33%': 8,
          '50%': 16,
          '66.66%': 32,
          '83.32%': 64,
          'max': 128
        },
        pips: {
          mode: 'range',
          density: 16,
          format: {
            to: function (value) {
              return value + 'GB';
            },
            from: function (value) {
              return Number(value.replace('GB', ''));
            }
          }
        },
        format: {
          to: function (value) {
            return value;
          },
          from: function (value) {
            return Number(value);
          }
        }
      });

      this.$refs.RAMSlider.noUiSlider.on('update', () => {
          this.selectedRAM = this.$refs.RAMSlider.noUiSlider.get();
      });
    },
    initStorageSlider() {
      noUiSlider.create(this.$refs.StorageSlider, {
        start: 50,
        animate: true,
        tooltips: [false],
        connect: [true, false],
        range: {
          'min': [50, 10], // must supply step values for both sides of the slider
          '50%': [500, 10],
          'max': 1000
        },
        pips: {
          mode: 'range',
          values: 3,
          format: {
            to: function (value) {
              return parseInt(value) + 'GB';
            },
            from: function (value) {
              return parseInt(value.replace('GB', ''));
            }
          }
        },
        format: {
          to: function (value) {
            return parseInt(value);
          },
          from: function (value) {
            return parseInt(value);
          }
        }
      });

      this.$refs.StorageSlider.noUiSlider.on('update', () => {
          this.selectedStorage = this.$refs.StorageSlider.noUiSlider.get();
      });
    },
    submit_deploy_vm() {
      this.spin = true;
      this.error_message = null; // ensure the error message is new each time

      if(!this.isBillingSufficient()) {
        this.spin = false;
        this.error_message = 'Insufficient funds to create this VM. Please add a card to your account or add credit via PayPal.'
        UIkit.modal('#CreateVMErrorModal').show();
      } else {
        let vm = {
          hostname: this.hostname,
          ramSize: this.selectedRAM,
          osType: this.selectedOS.version.osType,
          vcpus: this.selectedCores,
          storage: this.selectedStorage
        }

        this.CREATE_VIRTUAL_MACHINE(vm)
          .then( () => {
            this.spin = false;
            this.$router.push( { name: 'VirtualMachines' } );
          })
          .catch( (error) => {
            this.spin = false;
            this.error_message = error;
            UIkit.modal('#CreateVMErrorModal').show();
          })
      }
    }
  },
  mounted() {
    this.initCoreSlider();
    this.initRAMSlider();
    this.initStorageSlider();
    this.error_message = null;
  }
}
</script>

<style scoped lang="less">
@import "../../../assets/less/theme.less";

.ba-position-bottom {
  position: absolute;
  bottom: 30px;
  height: 42px;
}
.uk-tile {
  padding-top: 40px;
  padding-bottom: 40px;
}
.uk-tile-primary {
  background: linear-gradient(to right, @ba-purple, lighten(@ba-purple, 10%));
}
.uk-form-controls {
  padding-top: 5px;
}
.noUiSlider {
  margin-bottom: 40px;
}
</style>
